import { cs } from '../../lib/currency';
/**
 * Public Programs Page
 * 
 * Displays available cricket training programs for public browsing.
 * Includes Schema.org structured data for SEO.
 * No authentication required.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  BookOpen,
  Clock,
  Users,
  Calendar,
  ArrowLeft,
  Loader2,
  CheckCircle,
} from 'lucide-react';
import Footer from '../../components/Footer';
import _StructuredData, { 
  useStructuredData, 
  getCourseSchema,
  getOrganizationSchema,
  getBreadcrumbSchema
} from '../../components/StructuredData';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const PublicPrograms = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [error, setError] = useState(null);
  const [businessLogo, setBusinessLogo] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);

  const homeRoute = (() => {
    if (!authUser) return '/';
    return {
      admin: '/admin',
      staff: '/staff',
      coach: '/coach',
      parent: '/parent',
      customer: '/book',
      player: '/player'
    }[authUser.role] || '/';
  })();

  useEffect(() => {
    fetchPrograms();
    fetchBusinessInfo();
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const [logoRes, contactRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/logo`).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/contact-info`).then(r => r.json()).catch(() => ({}))
      ]);
      if (logoRes.logo_url) setBusinessLogo(logoRes.logo_url);
      setContactInfo(contactRes);
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${API_URL}/api/programs/public/list`);
      if (!response.ok) throw new Error('Failed to fetch programs');
      const data = await response.json();
      setPrograms(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching programs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Generate structured data schemas
  const tenantDisplayName = contactInfo?.business_name || 'Cricket Academy';
  const schemas = [
    getOrganizationSchema(contactInfo || {}, tenantDisplayName),
    getBreadcrumbSchema([
      { name: 'Home', url: window.location.origin },
      { name: 'Programs', url: `${window.location.origin}/programs` }
    ]),
    // ItemList of all programs
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Cricket Training Programs",
      "description": `Professional cricket coaching programs for all ages and skill levels at ${tenantDisplayName}`,
      "numberOfItems": programs.length,
      "itemListElement": programs.map((program, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": getCourseSchema(program, tenantDisplayName)
      }))
    }
  ];

  // Inject structured data
  useStructuredData(programs.length > 0 ? schemas : null);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {businessLogo ? (
              <img 
                src={`${API_URL}${businessLogo}`} 
                alt={contactInfo?.business_name || "Cricket Academy"} 
                className="h-10 w-auto cursor-pointer"
                onClick={() => navigate(homeRoute)}
              />
            ) : null}
            <span className="text-white font-semibold hidden sm:block">{contactInfo?.business_name || ''}</span>
          </div>
          {authUser ? (
            <Button
              onClick={() => navigate(homeRoute)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="back-to-dashboard-btn"
            >
              Back to Dashboard
            </Button>
          ) : (
            <Button 
              onClick={() => navigate('/?tab=login')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Login / Register
            </Button>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-800 to-slate-900 py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate(homeRoute)}
            className="text-slate-400 hover:text-white mb-4"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {authUser ? 'Back to Dashboard' : 'Back to Home'}
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Cricket Training Programs
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Professional cricket coaching programs designed for players of all ages and skill levels. 
            Expert instruction, structured curriculum, and proven results.
          </p>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {error ? (
            <Card className="bg-red-900/20 border-red-800">
              <CardContent className="py-8 text-center">
                <p className="text-red-400">{error}</p>
                <Button 
                  onClick={fetchPrograms} 
                  className="mt-4"
                  variant="outline"
                >
                  Try Again
                </Button>
              </CardContent>
            </Card>
          ) : programs.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No programs available at this time.</p>
                <Button 
                  onClick={() => navigate('/')} 
                  className="mt-4"
                >
                  Contact Us
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {programs.map((program) => (
                <Card 
                  key={program.id}
                  className="bg-slate-800 border-slate-700 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10"
                  data-testid={`program-card-${program.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-white flex items-center gap-2">
                          <BookOpen className="h-5 w-5 text-blue-400" />
                          {program.name}
                        </CardTitle>
                        {program.age_group && (
                          <Badge variant="outline" className="mt-2 text-slate-400 border-slate-600">
                            {program.age_group}
                          </Badge>
                        )}
                      </div>
                      {program.program_type === 'fixed_duration' && (
                        <Badge className="bg-purple-500/20 text-purple-300">
                          Limited Time
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="text-slate-400 mt-2">
                      {program.description || 'Professional cricket training program'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Program Details */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Clock className="h-4 w-4" />
                        <span>{program.duration || 60} minutes per session</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Users className="h-4 w-4" />
                        <span>Max {program.max_students || 20} students per class</span>
                      </div>
                    </div>

                    {/* Classes */}
                    {program.classes && program.classes.length > 0 && (
                      <div className="pt-3 border-t border-slate-700">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Available Classes</p>
                        <div className="space-y-1">
                          {program.classes.slice(0, 3).map((cls) => (
                            <div 
                              key={cls.id}
                              className="flex items-center gap-2 text-sm text-slate-300"
                            >
                              <Calendar className="h-3 w-3 text-blue-400" />
                              <span className="capitalize">{cls.schedule_day}</span>
                              <span className="text-slate-500">
                                {cls.start_time} - {cls.end_time}
                              </span>
                            </div>
                          ))}
                          {program.classes.length > 3 && (
                            <p className="text-xs text-slate-500">
                              +{program.classes.length - 3} more classes
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pricing */}
                    <div className="pt-3 border-t border-slate-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Starting at</p>
                          <p className="text-2xl font-bold text-white">
                            {cs()}{program.price || 0}
                            <span className="text-sm font-normal text-slate-400">/mo</span>
                          </p>
                        </div>
                        {(program.quarterly_price || program.biannual_price) && (
                          <div className="text-right">
                            <p className="text-xs text-green-400">Save with longer plans</p>
                            {program.quarterly_price && (
                              <p className="text-xs text-slate-500">{cs()}{program.quarterly_price}/qtr</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CTA */}
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate('/?tab=trial')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Request Trial Class
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-900 to-slate-900 py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Your Cricket Journey?
          </h2>
          <p className="text-slate-300 mb-6">
            Book a free trial class and experience our professional coaching firsthand.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => navigate('/?tab=trial')}
            >
              Book Free Trial
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/?tab=register')}
            >
              Create Account
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PublicPrograms;
